import { DailySchedule, SiteDaySchedule, TimeAndAttendanceLocation } from '../types';

export const useSchedule = () => {
  const isClockInLate = (schedule: SiteDaySchedule[], clockInTime: Date): number => {
    if (!schedule || schedule.length === 0) {
      return 0;
    }

    const clockInHours = clockInTime.getHours();
    const clockInMinutes = clockInTime.getMinutes();

    const basicSchedule = schedule.find(schedule => schedule.hour_type === 'basic');
    const overtimeSchedule = schedule.find(schedule => schedule.hour_type === 'overtime');

    if (basicSchedule && overtimeSchedule) {
      if (
        clockInHours > overtimeSchedule.start.hours ||
        (clockInHours === overtimeSchedule.start.hours && clockInMinutes >= overtimeSchedule.start.minutes)
      ) {
        if (
          clockInHours < overtimeSchedule.end.hours ||
          (clockInHours === overtimeSchedule.end.hours && clockInMinutes <= overtimeSchedule.end.minutes)
        ) {
          const minutesLate =
            (clockInHours - overtimeSchedule.start.hours) * 60 +
            (clockInMinutes - overtimeSchedule.start.minutes);
          return minutesLate;
        }
      }

      if (
        clockInHours > basicSchedule.start.hours ||
        (clockInHours === basicSchedule.start.hours && clockInMinutes >= basicSchedule.start.minutes)
      ) {
        if (
          clockInHours < basicSchedule.end.hours ||
          (clockInHours === basicSchedule.end.hours && clockInMinutes <= basicSchedule.end.minutes)
        ) {
          const minutesLate =
            (clockInHours - basicSchedule.start.hours) * 60 + (clockInMinutes - basicSchedule.start.minutes);
          return minutesLate;
        }
      }
    }

    const firstSchedule = schedule[0];
    const minutesLate =
      (clockInHours - firstSchedule.start.hours) * 60 + (clockInMinutes - firstSchedule.start.minutes);
    return minutesLate;
  };

  const isClockOutEarly = (schedule: SiteDaySchedule[], clockOutTime: Date): number => {
    if (!schedule || schedule.length === 0) {
      return 0;
    }

    const clockOutHours = clockOutTime.getHours();
    const clockOutMinutes = clockOutTime.getMinutes();

    const basicSchedule = schedule.find(schedule => schedule.hour_type === 'basic');
    const overtimeSchedule = schedule.find(schedule => schedule.hour_type === 'overtime');

    if (basicSchedule && overtimeSchedule) {
      if (
        clockOutHours > overtimeSchedule.start.hours ||
        (clockOutHours === overtimeSchedule.start.hours && clockOutMinutes >= overtimeSchedule.start.minutes)
      ) {
        if (
          clockOutHours < overtimeSchedule.end.hours ||
          (clockOutHours === overtimeSchedule.end.hours && clockOutMinutes <= overtimeSchedule.end.minutes)
        ) {
          const minutesEarly =
            (overtimeSchedule.end.hours - clockOutHours) * 60 +
            (overtimeSchedule.end.minutes - clockOutMinutes);
          return minutesEarly;
        }
      }

      if (
        clockOutHours > basicSchedule.start.hours ||
        (clockOutHours === basicSchedule.start.hours && clockOutMinutes >= basicSchedule.start.minutes)
      ) {
        if (
          clockOutHours < basicSchedule.end.hours ||
          (clockOutHours === basicSchedule.end.hours && clockOutMinutes <= basicSchedule.end.minutes)
        ) {
          const minutesEarly =
            (basicSchedule.end.hours - clockOutHours) * 60 + (basicSchedule.end.minutes - clockOutMinutes);
          return minutesEarly;
        }
      }
    }

    const firstSchedule = schedule[0];
    const minutesEarly =
      (firstSchedule.end.hours - clockOutHours) * 60 + (firstSchedule.end.minutes - clockOutMinutes);
    return minutesEarly;
  };

  const getShiftSchedule = (shiftId: string, locations: TimeAndAttendanceLocation[]) => {
    const shift = locations
      .find(location => location.shifts?.some(shift => shift.uuid === shiftId))
      ?.shifts?.find(shift => shift.uuid === shiftId);
    if (!shift) {
      return [];
    }

    const today = new Date();
    const dayOfWeek = today.getDay();

    const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    const todayName = dayNames[dayOfWeek] as keyof typeof shift.regular.schedule;

    const regularSchedule = convertDailyScheduleToSiteDaySchedule(shift.regular.schedule[todayName]);
    const overtimeSchedule = convertDailyScheduleToSiteDaySchedule(shift.overtime.schedule[todayName]);
    return [regularSchedule, overtimeSchedule];
  };

  const convertDailyScheduleToSiteDaySchedule = (dailySchedule: DailySchedule): SiteDaySchedule => {
    const [startHours, startMinutes] = dailySchedule.start.split(':').map(Number);
    const [endHours, endMinutes] = dailySchedule.end.split(':').map(Number);

    return {
      hour_type: dailySchedule.hour_type || 'basic',
      start: { hours: startHours, minutes: startMinutes },
      end: { hours: endHours, minutes: endMinutes },
    };
  };

  return {
    isClockInLate,
    isClockOutEarly,
    getShiftSchedule,
    convertDailyScheduleToSiteDaySchedule,
  };
};
